import { Download, Link, Sparkles, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Link as RouterLink } from "react-router-dom";
import { useTranslation } from "@/i18n";

const HowItWorks = () => {
  const { t } = useTranslation();

  const steps = [
    {
      icon: Link,
      step: "1",
      titleKey: "howItWorks.steps.connect.title",
      descriptionKey: "howItWorks.steps.connect.description",
      timeKey: "howItWorks.steps.connect.time",
      gradient: "from-primary to-primary/60",
    },
    {
      icon: Download,
      step: "2",
      titleKey: "howItWorks.steps.install.title",
      descriptionKey: "howItWorks.steps.install.description",
      timeKey: "howItWorks.steps.install.time",
      gradient: "from-accent to-accent/60",
    },
    {
      icon: Sparkles,
      step: "3",
      titleKey: "howItWorks.steps.generate.title",
      descriptionKey: "howItWorks.steps.generate.description",
      timeKey: "howItWorks.steps.generate.time",
      gradient: "from-primary to-accent",
    },
  ];

  return (
    <section id="how-it-works" className="py-20 md:py-32 relative overflow-hidden">
      {/* Background */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="glow-orb glow-orb-accent w-[600px] h-[600px] top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 opacity-15" />
      </div>

      <div className="container mx-auto px-4 relative">
        <div className="max-w-3xl mx-auto text-center mb-16">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full glass-card text-sm font-medium mb-6">
            <Sparkles size={16} className="text-accent" />
            <span className="text-foreground">{t('howItWorks.badge')}</span>
          </div>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-foreground mb-4">
            {t('howItWorks.title')}
          </h2>
          <p className="text-lg text-muted-foreground">
            {t('howItWorks.subtitle')}
            <br className="hidden md:block" />
            {t('howItWorks.subtitleLine2')}
          </p>
        </div>

        <div className="max-w-5xl mx-auto">
          {/* Timeline */}
          <div className="relative">
            {/* Connector line - desktop */}
            <div className="hidden md:block absolute top-24 left-0 right-0 h-0.5 bg-gradient-to-r from-primary via-accent to-primary opacity-30" />
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {steps.map((step, index) => (
                <div 
                  key={index} 
                  className="relative animate-fade-up opacity-0"
                  style={{ animationDelay: `${index * 150}ms` }}
                >
                  <div className="text-center">
                    {/* Step icon with number */}
                    <div className="relative inline-block mb-8">
                      <div className={`w-20 h-20 rounded-2xl bg-gradient-to-br ${step.gradient} flex items-center justify-center shadow-glow`}>
                        <step.icon className="w-9 h-9 text-white" />
                      </div>
                      <div className="absolute -top-2 -right-2 w-8 h-8 rounded-full bg-background border-2 border-primary text-primary flex items-center justify-center text-sm font-bold">
                        {step.step}
                      </div>
                    </div>
                    
                    {/* Content card */}
                    <div className="p-6 rounded-2xl glass-card border border-border/30 hover-lift">
                      <h3 className="text-xl font-bold text-foreground mb-3">
                        {t(step.titleKey)}
                      </h3>
                      <p className="text-muted-foreground text-sm leading-relaxed mb-4">
                        {t(step.descriptionKey)}
                      </p>
                      <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 text-primary text-sm font-medium">
                        ⏱️ {t(step.timeKey)}
                      </span>
                    </div>
                  </div>
                  
                  {/* Arrow for desktop */}
                  {index < steps.length - 1 && (
                    <div className="hidden md:flex absolute top-24 -right-4 z-10">
                      <ArrowRight className="w-6 h-6 text-muted-foreground" />
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* CTA */}
        <div className="text-center mt-16">
          <RouterLink to="/auth">
            <Button variant="hero" size="xl" className="group">
              {t('howItWorks.cta')}
              <ArrowRight size={20} className="transition-transform group-hover:translate-x-1" />
            </Button>
          </RouterLink>
          <p className="text-sm text-muted-foreground mt-4">
            {t('howItWorks.ctaNote')}
          </p>
        </div>
      </div>
    </section>
  );
};

export default HowItWorks;
